import {Menu, Product} from "@/data/entities/menu";
import {ProductSelection, ProductSelectionGroup, ProductSelectionOption} from "@/data/entities/cart/index";
import {findOptionById, findOptionGroupById} from "@/data/entities/option/utils";
import {OptionGroup, ProductOption} from "@/data/entities/option";
import {FulfillmentType, RestaurantDeliveryArea} from "../restaurant";
import {add, dinero, Dinero, EUR, lessThan, multiply, subtract, toDecimal, toSnapshot} from "dinero.js";
import {getProductPrice, stringToDinero} from "@/utils/MoneyUtils";
import {AddToCartProduct, AddToCartProductSideDish} from "@/data/entities/product";

export const generateInitialProductSelection = (product: Product, menu: Menu): ProductSelection => ({
    productExternalId: product.externalId,
    price: product.price,
    priceTakeaway: product.priceTakeaway,
    count: 1,
    groups: product.optionGroupIds.map((groupId: number) => {
        const optionGroup: OptionGroup = findOptionGroupById(groupId, menu.optionGroups);
        const isRequired = optionGroup.type === "required";
        const canSelectMultiple = !isRequired && optionGroup.maxOptions !== 1;
        return {
            groupExternalId: optionGroup.externalId,
            groupName: optionGroup.name,
            maxOptions: optionGroup.maxOptions,
            canSelectMultiple,
            isRequired,
            options: optionGroup.optionIds.map((optionId: number, index: number) => {
                const option: ProductOption = findOptionById(optionId, menu.options);
                return {
                    optionName: option.name,
                    optionExternalId: option.externalId,
                    soldout: option.soldout,
                    deposit: option.deposit,
                    availability: option.availability,
                    price: option.price,
                    priceTakeaway: option.priceTakeaway,
                    selected: !canSelectMultiple && index === 0, // When required, we can only pick one, thus the first one is the default selected
                };
            }),
        };
    })
});

export const selectionToTotalPrice = (
    selection: ProductSelection,
    fulfillmentType: FulfillmentType,
): string => {
    // Base price
    const productDinero: Dinero<number> = stringToDinero(getProductPrice(selection, fulfillmentType))

    // Side dishes
    const productAndSideDishesDinero: Dinero<number> =
        selection.groups
            .reduce((prevG, g: ProductSelectionGroup) => add(
                g.options
                    .filter(o => o.selected)
                    .reduce((prevO, o: ProductSelectionOption) => add(
                        stringToDinero(getProductPrice(o, fulfillmentType)),
                        prevO
                    ), stringToDinero("0")),
                prevG,
            ), productDinero)

    // Multiply by count
    const totalDinero = multiply(productAndSideDishesDinero, selection.count);

    // Note: this is the price ex. deposit & SUP-fee, that's only displayed in the cart
    return toDecimal(totalDinero);
};

export const selectionToAddToCartProduct = (
    selection: ProductSelection,
): AddToCartProduct => {
    const sideDishes: AddToCartProductSideDish[] = [];
    selection.groups.forEach((group: ProductSelectionGroup) => {
        group.options.forEach((option: ProductSelectionOption) => {
            if (option.selected) {
                sideDishes.push({
                    optionGroupExternalId: group.groupExternalId,
                    externalId: option.optionExternalId,
                });
            }
        });
    });

    return {
        externalId: selection.productExternalId,
        count: selection.count,
        sideDishes,
        loyaltyRewardId: null,
    }
};

const sideDishKey = (s: AddToCartProductSideDish): string =>
    `${s.optionGroupExternalId}:${s.externalId}`;

/**
 * Whether both have the same product and the same options (in any order).
 */
export const isSameProduct = (a: AddToCartProduct, b: AddToCartProduct): boolean => {
    if (a.externalId !== b.externalId)
        return false;

    if ((a.loyaltyRewardId ?? null) !== (b.loyaltyRewardId ?? null))
        return false;

    const aKeys = a.sideDishes.map(sideDishKey).sort();
    const bKeys = b.sideDishes.map(sideDishKey).sort();
    return aKeys.length === bKeys.length && aKeys.every((key, i) => key === bKeys[i]);
};

/**
 * Adds a product to the list, or increases the count of an identical one.
 */
export const addProduct = (products: AddToCartProduct[], toAdd: AddToCartProduct): AddToCartProduct[] => {
    const index = products.findIndex(p => isSameProduct(p, toAdd));
    if (index === -1)
        return [...products, toAdd];

    return products.map((p, i) => i === index
        ? {...p, count: p.count + toAdd.count}
        : p);
};

/**
 * Sets the count of a product line. Removes it when the count is below 1.
 */
export const setProductCount = (products: AddToCartProduct[], index: number, count: number): AddToCartProduct[] =>
    products
        .map((p, i) => i === index ? {...p, count} : p)
        .filter(p => p.count > 0);

/**
 * Menu products, groups and options by external ID, to display & price the
 * products of an order (which only hold external IDs).
 */
export type MenuLookup = {
    products: Map<string, Product>,
    optionGroups: Map<string, OptionGroup>,
    options: Map<string, ProductOption>,
};

export const createMenuLookup = (menu: Pick<Menu, "products" | "optionGroups" | "options">): MenuLookup => ({
    products: new Map(menu.products.map(p => [p.externalId, p])),
    optionGroups: new Map(menu.optionGroups.map(g => [g.externalId, g])),
    options: new Map(menu.options.map(o => [o.externalId, o])),
});

/**
 * Whether the product was redeemed with loyalty points (only on the website;
 * an edited order may keep it).
 */
export const isLoyaltyReward = (product: AddToCartProduct): boolean =>
    product.loyaltyRewardId !== null && product.loyaltyRewardId !== undefined;

/**
 * Price of a product line (product + side dishes, times count), ex. deposit
 * & SUP-fee. Unknown products/options (e.g. removed from the menu) count as 0.
 */
export const cartProductToTotalDinero = (
    product: AddToCartProduct,
    lookup: MenuLookup,
    fulfillmentType: FulfillmentType,
): Dinero<number> => {
    const menuProduct: Product | undefined = lookup.products.get(product.externalId);

    // Base price (free when redeemed with loyalty points)
    const productDinero: Dinero<number> = stringToDinero(
        menuProduct && !isLoyaltyReward(product) ? getProductPrice(menuProduct, fulfillmentType) : "0");

    // Side dishes
    const productAndSideDishesDinero: Dinero<number> = product.sideDishes
        .reduce((prev: Dinero<number>, sideDish: AddToCartProductSideDish) => {
            const option: ProductOption | undefined = lookup.options.get(sideDish.externalId);
            return add(
                stringToDinero(option ? getProductPrice(option, fulfillmentType) : "0"),
                prev,
            );
        }, productDinero);

    // Multiply by count
    return multiply(productAndSideDishesDinero, product.count);
};

/**
 * Total price of all product lines, ex. deposit & SUP-fee.
 */
export const cartToTotalDinero = (
    products: AddToCartProduct[],
    lookup: MenuLookup,
    fulfillmentType: FulfillmentType,
): Dinero<number> =>
    products.reduce(
        (prev: Dinero<number>, p: AddToCartProduct) => add(prev, cartProductToTotalDinero(p, lookup, fulfillmentType)),
        stringToDinero("0"),
    );

/**
 * Removes a product line.
 */
export const removeProduct = (products: AddToCartProduct[], index: number): AddToCartProduct[] =>
    products.filter((_, i) => i !== index);


export type CartTotals = {
    /**
     * Products & side dishes, ex. deposit, SUP-fee and delivery fee. Used for
     * the minimum order amount.
     */
    subtotal: Dinero<number>,
    deposit: Dinero<number>,
    supFee: Dinero<number>,
    /**
     * `null` for takeaway, or when the delivery area is unknown.
     */
    deliveryFee: Dinero<number> | null,
    /**
     * Only for website orders with a tip, when edited via the POS.
     */
    tip: Dinero<number>,
    total: Dinero<number>,
};

type PricedItem = {
    price: string,
    priceTakeaway: string,
    deposit: string,
    supFee: string,
};

/**
 * Totals of an order, calculated the same way as the backend
 * (`CartTotals.from`). POS orders have no tip; a website order that is
 * edited keeps its tip percentage.
 */
export const cartToTotals = (
    products: AddToCartProduct[],
    lookup: MenuLookup,
    fulfillmentType: FulfillmentType,
    deliveryArea: RestaurantDeliveryArea | null,
    tipPercentage: number = 0,
): CartTotals => {
    let subtotal: Dinero<number> = stringToDinero("0");
    let deposit: Dinero<number> = stringToDinero("0");
    let supFee: Dinero<number> = stringToDinero("0");

    products.forEach((product: AddToCartProduct) => {
        const items: (PricedItem | undefined)[] = [
            lookup.products.get(product.externalId),
            ...product.sideDishes.map((s: AddToCartProductSideDish) => lookup.options.get(s.externalId)),
        ];

        items.forEach((item: PricedItem | undefined, index: number) => {
            if (item === undefined)
                return;

            // A product redeemed with loyalty points is free; its options, deposit and SUP-fee are not
            if (!(index === 0 && isLoyaltyReward(product)))
                subtotal = add(subtotal, multiply(stringToDinero(getProductPrice(item, fulfillmentType)), product.count));
            deposit = add(deposit, multiply(stringToDinero(item.deposit), product.count));
            supFee = add(supFee, multiply(stringToDinero(item.supFee), product.count));
        });
    });

    const deliveryFee: Dinero<number> | null = fulfillmentType === "DELIVERY" && deliveryArea !== null
        ? stringToDinero(String(deliveryArea.deliveryFee))
        : null;

    const totalWithoutTip: Dinero<number> = [deposit, supFee, ...(deliveryFee ? [deliveryFee] : [])]
        .reduce((sum, d) => add(sum, d), subtotal);

    // Tip over everything, rounded half up to whole cents (like the backend)
    const tip: Dinero<number> = dinero({
        amount: Math.round(toSnapshot(totalWithoutTip).amount * tipPercentage / 100),
        currency: EUR,
    });
    const total: Dinero<number> = add(totalWithoutTip, tip);

    return {subtotal, deposit, supFee, deliveryFee, tip, total};
};

export type MinimumOrderCheck =
    | { status: "OK" }
    | { status: "NO_POSTAL_CODE" }
    | { status: "OUTSIDE_DELIVERY_AREA" }
    | { status: "BELOW_MINIMUM", minimum: Dinero<number>, remaining: Dinero<number> };

/**
 * Same check as the backend: for delivery, the postal code must be in a
 * delivery area and the subtotal must reach its minimum order amount.
 */
export const checkMinimumOrder = (
    subtotal: Dinero<number>,
    fulfillmentType: FulfillmentType,
    postalCodeDigits: string | null,
    deliveryArea: RestaurantDeliveryArea | null,
): MinimumOrderCheck => {
    if (fulfillmentType === "TAKEAWAY")
        return {status: "OK"};

    if (!postalCodeDigits)
        return {status: "NO_POSTAL_CODE"};

    if (deliveryArea === null)
        return {status: "OUTSIDE_DELIVERY_AREA"};

    const minimum: Dinero<number> = stringToDinero(String(deliveryArea.minimumOrderAmount));
    if (lessThan(subtotal, minimum))
        return {status: "BELOW_MINIMUM", minimum, remaining: subtract(minimum, subtotal)};

    return {status: "OK"};
};